import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlayCircle } from 'lucide-react';
import { theme } from '../../../../../theme';

const ReportFilters = ({ filters, onFiltersChange, onGenerateReport }) => {
  const { t } = useTranslation();

  const today = new Date().toISOString().split('T')[0];

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleDateChange = (key, value) => {
    let { start_date, end_date } = { ...filters };
    
    if (key === 'start_date') {
      start_date = value;
      if (end_date && new Date(end_date) < new Date(start_date)) {
        end_date = start_date;
      }
    } else {
      end_date = value;
    }

    onFiltersChange({ ...filters, start_date, end_date });
  };

  const ButtonGroup = ({ options, selected, onChange }) => (
    <div className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm">
      {options.map((option, index) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`
            px-4 py-2 text-sm font-semibold transition-colors
            ${selected === option.value ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}
            ${index === 0 ? 'rounded-l-md' : ''}
            ${index === options.length - 1 ? 'rounded-r-md' : 'border-r border-gray-300'}
          `}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            
            <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">{t('report.template')}:</label>
                <ButtonGroup
                    options={[
                        { value: 'basic', label: t('report.basic') },
                        { value: 'detailed', label: t('report.detailed') }
                    ]}
                    selected={filters.template}
                    onChange={(value) => handleFilterChange('template', value)}
                />
            </div>

            <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">{t('report.format')}:</label>
                <ButtonGroup
                    options={[
                        { value: 'pdf', label: t('report.pdf', 'PDF') },
                        { value: 'csv', label: t('report.csv', 'CSV') }
                    ]}
                    selected={filters.format || 'pdf'}
                    onChange={(value) => handleFilterChange('format', value)}
                />
            </div>

            <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">{t('report.startDate')}:</label>
                <input
                    type="date"
                    value={filters.start_date || ''}
                    onChange={(e) => handleDateChange('start_date', e.target.value)}
                    max={today}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>

            <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">{t('report.endDate')}:</label>
                <input
                    type="date"
                    value={filters.end_date || ''}
                    onChange={(e) => handleDateChange('end_date', e.target.value)}
                    min={filters.start_date || ''}
                    max={today}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>

            <div className="flex-grow"></div>

            <button
                onClick={onGenerateReport}
                style={{ background: `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.secondary[500]} 100%)`}}
                className="w-full sm:w-auto flex items-center justify-center gap-2 text-white px-6 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
            >
                <PlayCircle size={20} />
                <span>{t('report.generateReport')}</span>
            </button>
        </div>
    </div>
  );
};

export default ReportFilters;
